import { useCallback, useState } from 'react';

export interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  linkTo?: string;
  linkLabel?: string;
}

export interface ChecklistCategory {
  id: string;
  title: string;
  items: ChecklistItem[];
}

interface UseChecklistReturn {
  checkedIds: Set<string>;
  toggle: (id: string) => void;
  resetAll: () => void;
  overallProgress: number;
  checkedCount: number;
  totalItems: number;
  getCategoryProgress: (category: ChecklistCategory) => number;
}

export const CHECKLIST_CATEGORIES: readonly ChecklistCategory[] = [
  {
    id: 'pension',
    title: '연금 기반 다지기',
    items: [
      {
        id: 'pension-nps-verified',
        label: '국민연금 납부 이력을 확인했다',
        description: '국민연금공단 앱 또는 내연금알아보기(csa.nps.or.kr)에서 납부 기간과 예상 수령액을 확인하세요.',
        linkTo: '/pension',
        linkLabel: '국민연금 수령액 계산하기',
      },
      {
        id: 'pension-retirement-type-known',
        label: '퇴직연금 유형(DB/DC/IRP)을 파악했다',
        description: '회사 인사팀 또는 증권사 앱에서 확인할 수 있습니다. DC형은 직접 운용하므로 운용 현황도 점검하세요.',
      },
      {
        id: 'pension-personal-pension',
        label: '개인연금(연금저축·IRP)에 납입하고 있다',
        description: '국민연금과 퇴직연금만으로는 충분하지 않은 경우가 많습니다. 3층 연금 구조를 갖추는 것이 목표입니다.',
        linkTo: '/tax-benefit',
        linkLabel: 'IRP·ISA 절세 효과 계산하기',
      },
      {
        id: 'pension-total-monthly-known',
        label: '세 가지 연금을 합산한 월 예상 수령액을 알고 있다',
        description: '합산 수령액이 목표 생활비의 몇 %를 커버하는지 확인하세요. 부족분이 개인 저축의 목표가 됩니다.',
        linkTo: '/retirement',
        linkLabel: '노후 자금 계산기로 부족분 확인',
      },
    ],
  },
  {
    id: 'tax',
    title: '절세 계좌 활용',
    items: [
      {
        id: 'tax-irp-open',
        label: 'IRP 계좌를 개설하고 납입하고 있다',
        description: '연간 최대 900만 원까지 세액공제를 받을 수 있습니다. 미개설이라면 이달 안에 개설하세요.',
        linkTo: '/tax-benefit',
        linkLabel: 'IRP 절세 효과 계산',
      },
      {
        id: 'tax-isa-open',
        label: 'ISA 계좌를 개설했다',
        description: '이자·배당 수익 비과세 혜택이 있습니다. 소득이 있다면 지금 바로 개설해두면 가입 기간이 쌓입니다.',
        linkTo: '/tax-benefit',
        linkLabel: 'ISA 절세 효과 계산',
      },
      {
        id: 'tax-limit-checked',
        label: '연금저축 세액공제 한도를 확인했다',
        description: '연금저축펀드와 IRP를 합산해 연 900만 원이 세액공제 한도입니다. 연말정산 전에 한도를 채웠는지 점검하세요.',
        linkTo: '/tax-benefit',
        linkLabel: '세제혜택 계산기 실행',
      },
    ],
  },
  {
    id: 'investment',
    title: '자산 및 투자 점검',
    items: [
      {
        id: 'invest-asset-overview',
        label: '전체 자산 현황(부동산·금융·현금)을 파악하고 있다',
        description: '자산을 항목별로 정리해야 부족한 부분이 보입니다. 부동산 비중이 너무 높으면 유동성 리스크가 있습니다.',
      },
      {
        id: 'invest-allocation',
        label: '나이에 맞는 주식·채권 비율로 투자하고 있다',
        description: '은퇴까지 20년 이상 남았다면 주식 비중을 높게, 10년 이내라면 안전 자산 비중을 높이는 것이 일반적입니다.',
        linkTo: '/guide',
        linkLabel: '나이별 자산배분 가이드 보기',
      },
      {
        id: 'invest-diversified',
        label: '한 종목·한 자산에 집중 투자하고 있지 않다',
        description: '분산투자는 수익을 극대화하는 전략이 아니라 손실을 제한하는 안전장치입니다.',
        linkTo: '/guide',
        linkLabel: '분산투자 가이드 보기',
      },
      {
        id: 'invest-emergency-fund',
        label: '비상금을 생활비 3~6개월치 별도로 보유하고 있다',
        description: '비상금 없이 투자만 하면 갑작스러운 지출에 장기 투자를 깰 수 있습니다. 투자 계좌와 별도로 유지하세요.',
      },
      {
        id: 'invest-compound-sim',
        label: '복리 시뮬레이터로 은퇴 시점 자산을 계산했다',
        description: '현재 납입액이 목표 자산에 충분한지 숫자로 확인합니다.',
        linkTo: '/compound',
        linkLabel: '복리 시뮬레이터 실행',
      },
    ],
  },
  {
    id: 'expense',
    title: '지출 및 부채 관리',
    items: [
      {
        id: 'expense-monthly-target',
        label: '은퇴 후 예상 월 생활비를 구체적으로 계산했다',
        description: '막연한 숫자가 아닌 실제 지출 항목(주거·식비·의료·여가)을 기반으로 계산해야 계획이 현실적입니다.',
        linkTo: '/retirement',
        linkLabel: '노후 자금 계산기로 시뮬레이션',
      },
      {
        id: 'expense-debt-plan',
        label: '주택담보대출 상환 계획이 은퇴 전에 완료된다',
        description: '은퇴 후 고정 부채가 있으면 현금 흐름이 크게 제약됩니다. 은퇴 전 상환 완료를 목표로 하세요.',
      },
      {
        id: 'expense-inflation-checked',
        label: '인플레이션을 고려한 실질 필요 자금을 계산했다',
        description: '명목 목표가 아닌 물가 반영 실질 목표로 계획을 세웁니다.',
        linkTo: '/inflation',
        linkLabel: '물가 상승 계산기 실행',
      },
    ],
  },
  {
    id: 'health',
    title: '건강 및 의료 준비',
    items: [
      {
        id: 'health-insurance',
        label: '실손의료보험에 가입되어 있다',
        description: '노후 의료비는 일반 물가보다 빠르게 오릅니다. 현재 보험의 보장 범위와 갱신 조건을 확인하세요.',
      },
      {
        id: 'health-ltc',
        label: '노인장기요양보험 또는 간병 비용을 대비하고 있다',
        description: '65세 이후 약 20%가 요양 서비스를 필요로 합니다. 별도 적립이나 보험으로 대비하는 것을 권장합니다.',
      },
    ],
  },
  {
    id: 'plan',
    title: '계획 수립 및 점검',
    items: [
      {
        id: 'plan-target-set',
        label: '은퇴 목표 자금을 구체적인 숫자로 정했다',
        description: '"어느 정도면 되겠지"보다 "X억 원"이라는 구체적인 숫자가 있어야 저축 행동이 달라집니다.',
        linkTo: '/retirement/goal',
        linkLabel: '목표 설계 계산기로 계산',
      },
      {
        id: 'plan-depletion-sim',
        label: '자산 소진 나이 시뮬레이션을 확인했다',
        description: '보유 자산이 몇 세까지 버티는지 계산합니다.',
        linkTo: '/retirement',
        linkLabel: '노후 자금 계산기로 확인',
      },
      {
        id: 'plan-annual-review',
        label: '노후 준비 계획을 1년에 한 번 이상 점검하고 있다',
        description: '소득·지출·시장 상황이 바뀔 때마다 계획도 조정해야 합니다. 매년 생일 또는 연말을 기점으로 점검하세요.',
      },
    ],
  },
];

export const ALL_ITEM_IDS: string[] = CHECKLIST_CATEGORIES.flatMap((category) =>
  category.items.map((item) => item.id)
);

const STORAGE_KEY = 'nodudunden_checklist_v1';

export function useChecklist(): UseChecklistReturn {
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  const toggle = useCallback((id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(next)));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setCheckedIds(new Set());
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  const totalItems = ALL_ITEM_IDS.length;
  const checkedCount = checkedIds.size;
  const overallProgress = totalItems > 0 ? (checkedCount / totalItems) * 100 : 0;

  const getCategoryProgress = useCallback(
    (category: ChecklistCategory): number => {
      const count = category.items.filter((item) => checkedIds.has(item.id)).length;
      return category.items.length > 0 ? (count / category.items.length) * 100 : 0;
    },
    [checkedIds]
  );

  return {
    checkedIds,
    toggle,
    resetAll,
    overallProgress,
    checkedCount,
    totalItems,
    getCategoryProgress,
  };
}
